// src/pages/app/ConversationDetailsPage.tsx
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ConversationMessage,
  fetchConversationMessages,
  ConversationDetails,
  fetchConversationDetails,
  sendTestConversationMessage,
  sendManualConversationMessage,
  setConversationMode,
  ConversationMode } from "../../api/bots";
import { useTranslation } from "react-i18next";

const ConversationDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [details, setDetails] = useState<ConversationDetails | null>(null);

  const [testText, setTestText] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testSuccess, setTestSuccess] = useState<string | null>(null);


   const [humanText, setHumanText] = useState("");
  const [sendingHuman, setSendingHuman] = useState(false);
  const [humanError, setHumanError] = useState<string | null>(null);
  const [humanSuccess, setHumanSuccess] = useState<string | null>(null);

  const [updatingMode, setUpdatingMode] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);


  const is24hRestrictedChannel = !!(
    details &&
    (details.channel === "WHATSAPP" ||
      details.channel === "FACEBOOK" ||
      details.channel === "INSTAGRAM")
  );

  let outside24hWindow = false;

  if (details && is24hRestrictedChannel) {
    const lastUserAt = details.lastUserMessageAt
      ? new Date(details.lastUserMessageAt)
      : null;

    if (!lastUserAt) {
      outside24hWindow = true;
    } else {
      const diffMs = Date.now() - lastUserAt.getTime();
      const hours = diffMs / (1000 * 60 * 60);
      outside24hWindow = hours > 24;
    }
  }

 useEffect(() => {
  if (!id) return;
  setLoading(true);
  setError(null);

  Promise.all([
    fetchConversationMessages(id),
    fetchConversationDetails(id)
  ])
    .then(([messagesData, detailsData]) => {
      setMessages(messagesData);
      setDetails(detailsData);
    })
    .catch((err: any) => {
      console.error(err);
      setError(err?.message || t("conversationDetail.errors.loadFailed"));
    })
    .finally(() => setLoading(false));
}, [id, t]);

  if (!id) {
    return (
      <div className="page-container">
        <p>{t("conversationDetail.missingId")}</p>
      </div>
    );
  }

  const handleSendTest = async () => {
  if (!id) return;
  const trimmed = testText.trim();
  if (!trimmed) {
    setTestError(t("conversationDetail.testSend.empty"));
    setTestSuccess(null);
    return;
  }

  try {
    setSendingTest(true);
    setTestError(null);
    setTestSuccess(null);

    await sendTestConversationMessage(id, trimmed);
    setTestSuccess(t("conversationDetail.testSend.success"));
    setTestText("");

    // Refresh messages so the new assistant message appears
    fetchConversationMessages(id)
      .then((data) => setMessages(data))
      .catch((err) => {
        console.error("Failed to refresh messages after test send", err);
      });
  } catch (err: any) {
    console.error(err);
    setTestError(
      err?.message || t("conversationDetail.testSend.error")
    );
  } finally {
    setSendingTest(false);
  }
};

const handleSwitchMode = async (nextMode: ConversationMode) => {
  if (!id || !details) return;
  setUpdatingMode(true);
  setModeError(null);
  try {
    const res = await setConversationMode(id, nextMode);
    setDetails({ ...details, mode: res.mode });
  } catch (err: any) {
    console.error(err);
    setModeError(
      err?.message ||
        t("conversationDetail.mode.error") ||
        "Failed to update conversation mode."
    );
  } finally {
    setUpdatingMode(false);
  }
};


const handleSendHuman = async () => {
    if (!id) return;
    const trimmed = humanText.trim();
    if (!trimmed) {
      setHumanError(t("conversationDetail.testSend.empty"));
      setHumanSuccess(null);
      return;
    }

    if (outside24hWindow) {
      // Just in case, double-guard in the UI too
      setHumanError(
        t("conversationDetail.manualSend.windowExpired") ||
          "You can no longer send a manual reply: the last user message is older than 24 hours."
      );
      setHumanSuccess(null);
      return;
    }

    try {
      setSendingHuman(true);
      setHumanError(null);
      setHumanSuccess(null);

      await sendManualConversationMessage(id, trimmed);
      setHumanSuccess(
        t("conversationDetail.manualSend.success") ||
          "Message sent as HUMAN."
      );
      setHumanText("");

      // Refresh messages so the new HUMAN message appears
      fetchConversationMessages(id)
        .then((data) => setMessages(data))
        .catch((err) => {
          console.error("Failed to refresh messages after manual send", err);
        });
    } catch (err: any) {
      console.error(err);
      setHumanError(
        err?.message ||
          t("conversationDetail.testSend.error") ||
          "Failed to send manual message."
      );
    } finally {
      setSendingHuman(false);
    }
  };

  const roleLabel = (role: string) => {
    if (role === "USER") return t("conversationDetail.roles.user");
    if (role === "ASSISTANT") return t("conversationDetail.roles.assistant");
    if (role === "HUMAN") return t("conversationDetail.roles.human") || "Human";
    return t("conversationDetail.roles.system");
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t("conversationDetail.title")}</h1>
        <Link to="/app/bots" className="btn-secondary">
          {t("conversationDetail.backToBots")}
        </Link>
      </div>

      {details && (
  <div className="card" style={{ marginBottom: "1rem" }}>
    <h2 style={{ marginTop: 0 }}>
      {t("conversationDetail.mode.title") || "Conversation mode"}
    </h2>
    <p className="muted" style={{ marginBottom: "0.5rem" }}>
      {details.mode === "HUMAN"
        ? t("conversationDetail.mode.humanDescription") ||
          "Human handoff is active. The bot will not reply automatically to user messages."
        : t("conversationDetail.mode.aiDescription") ||
          "The AI assistant replies automatically to new user messages."}
    </p>

    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
      <span style={{ fontWeight: 600 }}>
        {details.mode === "HUMAN"
          ? t("conversationDetail.mode.humanLabel") || "HUMAN"
          : t("conversationDetail.mode.aiLabel") || "AI"}
      </span>

      <button
        type="button"
        className="btn-secondary"
        disabled={updatingMode || details.mode === "HUMAN"}
        onClick={() => handleSwitchMode("HUMAN")}
      >
        {updatingMode && details.mode === "AI"
          ? t("conversationDetail.mode.switchingToHuman") || "Switching…"
          : t("conversationDetail.mode.toHumanButton") ||
            "Switch to HUMAN (pause AI)"}
      </button>

      <button
        type="button"
        className="btn-secondary"
        disabled={updatingMode || details.mode === "AI"}
        onClick={() => handleSwitchMode("AI")}
      >
        {updatingMode && details.mode === "HUMAN"
          ? t("conversationDetail.mode.switchingToAi") || "Switching…"
          : t("conversationDetail.mode.toAiButton") || "Back to AI"}
      </button>
    </div>

    {modeError && (
      <div className="form-error" style={{ marginTop: "0.5rem" }}>
        {modeError}
      </div>
    )}
  </div>
)}

      {details  /* && details.channel !== "WEB" */ && (
        <div className="card" style={{ marginBottom: "1rem" }}>
          <h2 style={{ marginTop: 0 }}>
            {t("conversationDetail.manualSend.title") || "Manual reply (HUMAN)"}
          </h2>
          <p className="muted" style={{ marginBottom: "0.5rem" }}>
            {t("conversationDetail.manualSend.description") ||
              "Send a manual reply as a human agent over this channel."}
          </p>

          {is24hRestrictedChannel && outside24hWindow && (
            <div className="form-error" style={{ marginBottom: "0.5rem" }}>
              {t("conversationDetail.manualSend.windowExpired") ||
                "You can no longer send manual messages: the last user message is more than 24 hours old on this channel. Meta requires templates beyond the 24h window."}
            </div>
          )}

          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              type="text"
              value={humanText}
              onChange={(e) => setHumanText(e.target.value)}
              placeholder={
                t("conversationDetail.manualSend.placeholder") ||
                "Type a manual reply..."
              }
              style={{ flex: 1 }}
              disabled={sendingHuman || (is24hRestrictedChannel && outside24hWindow)}
            />
            <button
              type="button"
              className="btn-primary"
              onClick={handleSendHuman}
              disabled={sendingHuman || (is24hRestrictedChannel && outside24hWindow)}
            >
              {sendingHuman
                ? t("conversationDetail.manualSend.sending") || "Sending..."
                : t("conversationDetail.manualSend.button") || "Send"}
            </button>
          </div>

          {humanError && (
            <div className="form-error" style={{ marginTop: "0.5rem" }}>
              {humanError}
            </div>
          )}

          {humanSuccess && (
            <div className="form-success" style={{ marginTop: "0.5rem" }}>
              {humanSuccess}
            </div>
          )}
        </div>
      )}

      {loading && <p>{t("conversationDetail.loading")}</p>}
      {error && <div className="form-error">{error}</div>}

      {details && !loading && !error && (
    <div className="card" style={{ marginBottom: "1rem" }}>
    <h2 style={{ marginTop: 0 }}>{t("conversationDetail.parties.title")}</h2>
    <div
      className="conversation-parties"
      style={{
        display: "flex",
        gap: "1.5rem",
        flexWrap: "wrap",
        marginTop: "0.5rem"
      }}
    >
      <div style={{ flex: 1, minWidth: "200px" }}>
        <h3 style={{ marginBottom: "0.25rem" }}>
          {t("conversationDetail.parties.business")}
        </h3>
        <div className="muted">
          <div>{details.business.title}</div>
          {details.business.subtitle && (
            <div style={{ fontSize: "0.9em" }}>{details.business.subtitle}</div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: "200px" }}>
        <h3 style={{ marginBottom: "0.25rem" }}>
          {t("conversationDetail.parties.user")}
        </h3>
        <div className="muted">
          <div>
            {details.user.displayName || details.user.identifier}
          </div>
          {details.user.displayName && (
            <div style={{ fontSize: "0.85em", opacity: 0.8 }}>
            {details.user.identifier}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}

      {!loading && !error && messages.length === 0 && (
        <p>{t("conversationDetail.empty")}</p>
      )}

      {!loading && !error && messages.length > 0 && (
        <div className="conversation-view">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`conversation-message conversation-message-${m.role.toLowerCase()}`}
            >
              <div className="conversation-meta">
                <span className="conversation-role">{roleLabel(m.role)}</span>
                <span className="conversation-time">
                  {new Date(m.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="conversation-content">{m.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConversationDetailPage;
